import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { shape } from 'prop-types'

import { TENANT_IT } from 'shared/consts'
import { PRESCRIPTION_TYPES } from 'shared/consts/productTemplates'
import Badge from 'views/components/atoms/Badge'
import IconCircleCheck from 'views/components/atoms/Icon/IconCircleCheck'
import IconCirclePlus from 'views/components/atoms/Icon/IconCirclePlus'
import IconTick from 'views/components/atoms/Icon/IconTick'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import { RX_BONUS_MODAL } from 'views/components/organisms/Cart/RxBonusModal/const'
import './RedPointsAndRXInfoBox.scss'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'

const RedPointsAndRXInfoBox = ({ redpointsAndRx }) => {
  const bonusItems = []
  for (const sellerGroup of redpointsAndRx?.sellerGroups || []) {
    if (sellerGroup?.bonusItems?.length) {
      bonusItems.push(...sellerGroup.bonusItems)
    }
  }

  const {
    publicRuntimeConfig: {
      publicConfig: { tenant },
    },
  } = useShopAndRuntimeConfig()

  const isRebrandingITEnabled = tenant === TENANT_IT

  const hasERX = redpointsAndRx.sellerGroups.some(
    group => group.groupEntriesERx.length > 0
  )

  const hasPrivatePrescription = redpointsAndRx.sellerGroups.some(group =>
    group.groupEntries.some(item => item.type === PRESCRIPTION_TYPES.PRIVAT)
  )

  const showRxBonusLink = !hasERX && hasPrivatePrescription

  const hasPublicOrFreePrescription = redpointsAndRx.sellerGroups.some(group =>
    group.groupEntries.some(
      item =>
        item.type === PRESCRIPTION_TYPES.INSURANCE || PRESCRIPTION_TYPES.FREE
    )
  )

  const showRxBonusModal =
    hasERX || (hasPublicOrFreePrescription && !hasPrivatePrescription)

  return (
    <div className="a-box a-box--bordered o-Checkout__RedPointsAndRXInfoBox">
      {bonusItems.length > 0 && (
        <div className="a-p u-color--dark u-padding--bottom u-margin--bottom u-border--bottom o-Checkout__RedPointsAndRXInfoBox__top">
          <strong className="o-Checkout__RedPointsAndRXInfoBox__title">
            <Trans id="cart.redPoints.cartRules.headline" />
          </strong>
          {bonusItems.map((bonusItem, index) => {
            const textClasses = classNames(
              'u-padding-x-small--left u-display--inline-block o-Checkout__RedPointsAndRXInfoBox__top-text',
              {
                'u-color--positive': bonusItem.bonusApplied,
              }
            )

            return (
              <div
                key={index + bonusItem.bonusText}
                className="u-padding-small--top o-Checkout__RedPointsAndRXInfoBox__bonus-text-container"
                data-qa-id={
                  bonusItem.bonusApplied
                    ? `cart-bonus-item-applied`
                    : `cart-bonus-item-not-applied`
                }
              >
                <TextWithIcon
                  icon={
                    bonusItem.bonusApplied ? (
                      <IconCircleCheck size="small" />
                    ) : (
                      <IconCirclePlus size="small" />
                    )
                  }
                  alignIconToTop
                >
                  <span className={textClasses}>{bonusItem.bonusText}</span>
                </TextWithIcon>
              </div>
            )
          })}
        </div>
      )}
      {redpointsAndRx.redPointsTotalAmount > 0 && (
        <span className="u-padding-xx-small l-flex o-Checkout__RedPointsAndRXInfoBox__bottom">
          {isRebrandingITEnabled && <IconTick size="small" />}
          <Badge
            value={`+ ${redpointsAndRx.redPointsTotalAmount}`}
            color={'red'}
            ellipse
            className="u-no-padding u-no-wrap o-Checkout__RedpointsAndRXInfoBox__bottom--redpoints"
            dataQa="cart-badge-RedPointsSum"
          />
          <span className="u-margin--left o-Checkout__RedPointsAndRXInfoBox__bottom-text">
            <Trans id="cart.possible.redpoints" />
          </span>
        </span>
      )}
      {redpointsAndRx.rxBonusTotal > 0 && (
        <span
          className="u-padding-xx-small l-flex o-Checkout__RedPointsAndRXInfoBox__bottom"
          data-qa-id="rx-totalBonus-sum"
        >
          {isRebrandingITEnabled && <IconTick size="small" />}
          <Badge
            ellipse
            value={redpointsAndRx.rxBonusTotal}
            pricing
            className="u-no-padding u-no-wrap o-Checkout__RedpointsAndRXInfoBox__bottom--rxBonus"
            dataQa="cart-badge-rxDiscount"
            textcolor={'dark'}
            supNoteNumber="7" // Fallback for hydration
            showSupNotes
          />
          {showRxBonusLink && (
            <span className="u-margin--left o-Checkout__RedPointsAndRXInfoBox__bottom-text">
              <Trans
                id={'cart.prescriptionBonus.more.information.Rx'}
                components={{
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  link: <a href="/rezeptbonus.htm" target="_blank" />,
                }}
              />
            </span>
          )}
          {showRxBonusModal && (
            <span className="u-margin--left o-Checkout__RedPointsAndRXInfoBox__bottom-text">
              <Trans id={'cart.prescriptionBonus.more.information.eRx'} />
              <u
                data-a11y-dialog-show={RX_BONUS_MODAL}
                className="u-margin-xx-small--left o-Checkout__RedPointsAndRXInfoBox__link"
              >
                <Trans id="general.more.information.short" />
              </u>
            </span>
          )}
        </span>
      )}
    </div>
  )
}

RedPointsAndRXInfoBox.propTypes = {
  redpointsAndRx: shape({}),
}

RedPointsAndRXInfoBox.defaultProps = {
  redpointsAndRx: {},
}

export default RedPointsAndRXInfoBox
